import { useMemo, useState } from "react";
import { genreTabs, movies } from "@/mocks/movies";
import type { Movie } from "@/pages/home/types";
import SectionHeading from "@/pages/home/components/SectionHeading";
import MovieCard from "@/pages/home/components/MovieCard";

const allMovies = movies as Movie[];

export default function GenreExplorer() {
  const [activeTab, setActiveTab] = useState("全部");
  const [keyword, setKeyword] = useState("");

  const result = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return allMovies.filter((movie) => {
      const matchTab = activeTab === "全部" || movie.category === activeTab;
      if (!matchTab) return false;
      if (!kw) return true;
      const haystack = [movie.title, movie.director, movie.summary, ...movie.tags]
        .join(" ")
        .toLowerCase();
      return haystack.includes(kw);
    });
  }, [activeTab, keyword]);

  return (
    <section id="genre" className="scroll-mt-20 bg-background-100 py-14 md:py-20">
      <div className="mx-auto w-full max-w-[1280px] px-4 md:px-8">
        <SectionHeading
          eyebrow="类型片浏览器"
          title="按类型与关键词，在本页片单里直接筛选"
          desc="切换分类或输入关键词，筛选只作用于本页已收录的影片，不会联网请求片库，也不会上传你的输入。"
          accent="secondary"
        />

        <div className="mt-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="inline-flex flex-wrap gap-1 self-start rounded-full border border-background-300 bg-background-50 p-1">
            {genreTabs.map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === tab
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-700 hover:bg-primary-100 hover:text-primary-700"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="relative w-full lg:w-80">
            <span className="pointer-events-none absolute left-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-search-line text-base" />
            </span>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索片名、导演或标签关键词"
              aria-label="搜索影片"
              className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-10 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
            {keyword && (
              <button
                type="button"
                aria-label="清除关键词"
                onClick={() => setKeyword("")}
                className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-foreground-400 hover:bg-background-200 hover:text-foreground-700"
              >
                <i className="ri-close-line" />
              </button>
            )}
          </div>
        </div>

        <p className="mt-4 text-sm text-foreground-600">
          当前分类：
          <strong className="font-medium text-foreground-950">{activeTab}</strong>
          {keyword.trim() && (
            <>
              ，关键词：
              <strong className="font-medium text-foreground-950">{keyword.trim()}</strong>
            </>
          )}
          ，共找到 <strong className="font-medium text-primary-600">{result.length}</strong> 部影片。
        </p>

        {result.length > 0 ? (
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {result.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        ) : (
          <div className="mt-8 flex flex-col items-center gap-3 rounded-lg border border-dashed border-background-400 bg-background-50 py-14 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-movie-line text-2xl" />
            </span>
            <p className="font-heading text-lg font-bold text-foreground-950">没有匹配的影片</p>
            <p className="max-w-md text-sm text-foreground-600">
              试试更换分类，或改用更短的关键词，例如「剧情」「香港」「恐怖」。
            </p>
            <button
              type="button"
              onClick={() => {
                setActiveTab("全部");
                setKeyword("");
              }}
              className="mt-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              重置筛选条件
            </button>
          </div>
        )}
      </div>
    </section>
  );
}